import React,{ forwardRef, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Box } from '@mui/material';
import { BarChartDataItem, BaseBlockProps, ChartColorConfig } from '../types';
import {
    BAR_CHART_MARGINS,
    BAR_LEGEND_CONFIG,
    CHART_THEME,
    createAxisConfig,
} from '../../../utils/chartConfig';
import { OrdinalColorScaleConfig } from '@nivo/colors';

interface BarChartProps {
    data: BarChartDataItem[];
    keys: string[];
    indexBy?: string;
    colors?: ChartColorConfig | ((params: { id: string; data: Record<string, unknown> }) => string);
    groupMode?: 'stacked' | 'grouped';
    layout?: 'horizontal' | 'vertical';
    reverse?: boolean;
    enableLabels?: boolean;
    enableGrid?: boolean;
    kedo: BaseBlockProps["kedo"];
    legendDirection?: string;
    height?: string | number;
}

export const BarChart = forwardRef<HTMLDivElement, BarChartProps>(
    (
        {
            data,
            keys,
            indexBy = 'period',
            colors = { scheme: 'nivo' },
            groupMode = 'grouped',
            layout = 'vertical',
            reverse = false,
            enableLabels = false,
            enableGrid = true,
            kedo,
            legendDirection = 'left-to-right',
            height = '100%',
        },
        ref
    ) => {
        const bottomAxisConfig = useMemo(
            () => createAxisConfig(kedo.t('Date'), 'bottom'),
            [kedo]
        );

        const leftAxisConfig = useMemo(
            () => createAxisConfig(kedo.t('Totals'), 'left', true),
            [kedo]
        );

        const colorConfig = useMemo((): OrdinalColorScaleConfig => {
            if (typeof colors === 'function') {
                return colors  as OrdinalColorScaleConfig;
            }
            if (colors.scheme) {
                return { scheme: colors.scheme }  as OrdinalColorScaleConfig;
            }
            return { scheme: 'nivo' }  as OrdinalColorScaleConfig;
        }, [colors]);

        const legends = useMemo(
            () => [
                {
                    ...BAR_LEGEND_CONFIG,
                    dataFrom: 'keys' as const,
                    itemDirection: legendDirection as
                        | 'left-to-right'
                        | 'right-to-left'
                        | 'top-to-bottom'
                        | 'bottom-to-top',
                },
            ],
            [legendDirection]
        );

        return (
            <Box ref={ref} sx={{ height, width: '100%' }}>
                <ResponsiveBar
                    data={data}
                    keys={keys}
                    indexBy={indexBy}
                    margin={BAR_CHART_MARGINS}
                    padding={0.3}
                    groupMode={groupMode}
                    layout={layout}
                    reverse={reverse}
                    colors={colorConfig}
                    enableLabel={enableLabels}
                    enableGridX={enableGrid}
                    enableGridY={enableGrid}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={bottomAxisConfig}
                    axisLeft={leftAxisConfig}
                    legends={legends}
                    theme={CHART_THEME}
                    animate
                    motionConfig="gentle"
                />
            </Box>
        );
    }
);

BarChart.displayName = 'BarChart';
